import i18next from 'i18next'
import { DisqualifyIcon, PassIcon } from './icons'

const t = (key: string) => i18next.t(`editor:${key}`)

export const QUESTION_TYPE_OPTIONS = [{
	id: 'select',
	render: (inList: boolean) => (
		<div className="px-3 py-1 bg-blue-50 text-blue-700">
			<span className="font-medium">{t('formBuilder.questionTypes.singleSelect')}</span>
			{inList && (
				<div className="text-xs pr-3 text-grey-500">
					{t('formBuilder.questionTypes.singleSelectDescription')}
				</div>
			)}
		</div>
	),
}, {
	id: 'select_multiple',
	render: (inList: boolean) => (
		<div className="px-3 py-1 bg-purple-50 text-purple-700">
			<span className="font-medium">{t('formBuilder.questionTypes.multiSelect')}</span>
			{inList && (
				<div className="text-xs pr-3 text-grey-500">
					{t('formBuilder.questionTypes.multiSelectDescription')}
				</div>
			)}
		</div>
	),
}, {
	id: 'open_text',
	render: (inList: boolean) => (
		<div className="px-3 py-1 bg-teal-50 text-teal-700">
			<span className="font-medium">{t('formBuilder.questionTypes.openText')}</span>
			{inList && (
				<div className="text-xs pr-3 text-grey-500">{t('formBuilder.questionTypes.openTextDescription')}</div>
			)}
		</div>
	),
}] as const

export const WEIGHT_OPTIONS = [{
	id: '1',
	render: (inList: boolean) => (
		<div className="px-3 py-1 bg-grey-50 text-grey-700">
			<span className="font-medium">{t('formBuilder.weight.standard')}</span>
			{inList && <div className="text-xs pr-3 text-grey-500">{t('formBuilder.weight.standardDescription')}</div>}
		</div>
	),
}, {
	id: '2',
	render: (inList: boolean) => (
		<div className="px-3 py-1 bg-fuchsia-50 text-fuchsia-700">
			<span className="font-medium">{t('formBuilder.weight.important')}</span>
			{inList && <div className="text-xs pr-3 text-grey-500">{t('formBuilder.weight.importantDescription')}</div>}
		</div>
	),
}] as const

export const SCORE_OPTIONS = [{
	id: '4',
	render: () => (
		<div className="px-3 py-1 bg-green-50 text-green-700">
			<span className="font-medium">{t('formBuilder.score.excellent')}</span>
		</div>
	),
}, {
	id: '3',
	render: () => (
		<div className="px-3 py-1 bg-lime-50 text-lime-700">
			<span className="font-medium">{t('formBuilder.score.good')}</span>
		</div>
	),
}, {
	id: '2',
	render: () => (
		<div className="px-3 py-1 bg-yellow-50 text-yellow-700">
			<span className="font-medium">{t('formBuilder.score.okay')}</span>
		</div>
	),
}, {
	id: '1',
	render: () => (
		<div className="px-3 py-1 bg-amber-50 text-amber-700">
			<span className="font-medium">{t('formBuilder.score.poor')}</span>
		</div>
	),
}, {
	id: '0',
	render: () => (
		<div className="px-3 py-1 bg-red-50 text-red-700">
			<span className="font-medium">{t('formBuilder.score.bad')}</span>
		</div>
	),
}] as const

export const ELIGIBILITY_OPTIONS = [{
	id: 'no',
	render: () => (
		<div className="px-3 py-1 bg-grey-50 text-grey-700 font-medium flex items-center gap-2">
			<PassIcon />
			{t('formBuilder.eligibility.pass')}
		</div>
	),
}, {
	id: 'yes',
	render: (inList: boolean) => (
		<div className="px-3 py-1 bg-red-50 text-red-700 font-medium">
			<div className="flex items-center gap-2">
				<DisqualifyIcon />
				{t('formBuilder.eligibility.disqualify')}
			</div>
			{inList && (
				<span className="text-xs text-red-400">{t('formBuilder.eligibility.disqualifyDescription')}</span>
			)}
		</div>
	),
}] as const

export const REQUIRED_OPTIONS = [{
	id: 'yes',
	render: () => (
		<div className="px-3 py-1 bg-orange-50 text-orange-700 font-medium">
			{t('formBuilder.required.required')}
		</div>
	),
}, {
	id: 'no',
	render: (inList: boolean) => (
		<div className="px-3 py-1 bg-grey-50 text-grey-700 font-medium">
			{t('formBuilder.required.optional')}
			{inList && <div className="text-xs text-grey-500">{t('formBuilder.required.optionalDescription')}</div>}
		</div>
	),
}] as const

export const CONSENT_TYPE_OPTIONS = [{
	id: 'positive',
	render: (inList: boolean) => (
		<div className="px-3 py-1 bg-green-50 text-green-700 font-medium">
			{t('formBuilder.consent.positive')}
			{inList && <div className="text-xs text-grey-500">{t('formBuilder.consent.positiveDescription')}</div>}
		</div>
	),
}, {
	id: 'neutral',
	render: (inList: boolean) => (
		<div className="px-3 py-1 bg-blue-50 text-blue-700 font-medium">
			{t('formBuilder.consent.neutral')}
			{inList && <div className="text-xs text-grey-500">{t('formBuilder.consent.neutralDescription')}</div>}
		</div>
	),
}, {
	id: 'negative',
	render: (inList: boolean) => (
		<div className="px-3 py-1 bg-red-50 text-red-700 font-medium">
			{t('formBuilder.consent.negative')}
			{inList && (
				<div className="text-xs text-grey-500">
					{t('formBuilder.consent.negativeDescription')}
				</div>
			)}
		</div>
	),
}] as const

/** Generate max selection options dynamically based on option count */
export function generateMaxSelectionsOptions(optionCount: number) {
	const maxOptions = Math.max(1, optionCount)
	return Array.from({ length: maxOptions }, (_, i) => ({
		id: `${i + 1}`,
		render: () => (
			<div className="px-3 py-1 bg-grey-50 text-grey-700 font-medium">
				{i + 1}
			</div>
		),
	}))
}
